import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import AddonPageStyle from "../Addon/AddonPageStyle.module.scss";
import AddonsBillingSummary from "../../Components/AddonsBillingSummary/AddonsBillingSummary";
import AddonUsage from "../../Components/AddonUsage/AddonUsage";
import { ReactComponent as Addon } from "../../../../AdminAssets/svg/addonImage.svg";
import { ReactComponent as PerformanceSites } from "../../../../AdminAssets/svg/performanceSites.svg";
import { ReactComponent as Staging } from "../../../../AdminAssets/svg/stagingEnv.svg";
import { ReactComponent as WAF } from "../../../../AdminAssets/svg/waf.svg";
import { ReactComponent as StorageIcon } from "../../../../AdminAssets/svg/Storage_plan.svg";
import { ReactComponent as PerformanceUpgradeIcon } from "../../../../AdminAssets/svg/PerformanceSite.svg";
import { Actions } from "../../../../../../redux/constants/actionTypes";
import { getStore } from "../../../../../../Utils/storeUtils";
import { useMakeApiCalls } from "../../../../../../Hooks/useMakeApiCalls";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  addons_names,
  buildsV1Sites,
  lastSegmentPath,
} from "../../../../../../Utils/roconConstants";
import { Grid2 } from "@mui/material";
import FormPopup from "../../../../../../GlobalComponents/FormPopup/FormPopup";
import { formatIndianCurrency } from "../../../../../../Utils/utils";
import PageLoading from "../../../../../../GlobalComponents/PageLoading/PageLoading";

const AddonPage = () => {
  const navigate = useNavigate();
  const [stripePromise, setStripePromise] = useState(null);
  const [addonDetails, setAddonDetails] = useState([]);
  const [hours, setHours] = useState(0);
  const [price, setPrice] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  // Storage add-on: bought for one or more EBS sites in one checkout (same packs per site)
  const [storageSiteIds, setStorageSiteIds] = useState([]);
  // Performance upgrade: one EBS site at a time, one tier (t4g.small) for now
  const [performanceSiteId, setPerformanceSiteId] = useState("");
  const [siteOverviewList, setSiteOverviewList] = useState([]);
  const params = useParams();
  const orgId = params.orgId;
  const siteId = params.siteId;
  const location = useLocation();
  const lastSegment = lastSegmentPath(location);

  const selectedOrg = useSelector(
    (state) => state.organizationsInfo?.selectedOrg,
  );
  const userDetails = useSelector((state) => state.users?.userDetails);
  const country = selectedOrg?.country || userDetails?.country;
  const allPlans = useSelector((state) => state.plansInfo.allPlans);
  const paymentInfo = useSelector((state) => state.paymentInfo);
  const upcomingInvoice = paymentInfo?.upcomingInvoice;
  const plan = useSelector(
    (state) => state.plansInfo?.specificPlan?.specificPlans,
  );
  const isDisabled = addonDetails.every((addon) => addon.addonCount === 0);
  const stagingPlan = plan?.find((item) => item.licenseType === "Staging");
  const stagingLimit = stagingPlan?.featureDetails?.find(
    (feature) => feature?.type === "STAGING",
  )?.limitOrAvailablity;
  const stagingUsed =
    stagingPlan?.featureDetails?.find((feature) => feature?.type === "STAGING")
      ?.used || 0;
  const basePlan = plan?.find((item) => item.basePlan === true);
  const isSingleSite = basePlan?.isSingleSite;
  const limit = basePlan?.featureDetails?.find(
    (feature) => feature.type === "NUM_SITES",
  );

  const dedicatedHosting = useSelector(
    (state) => state.plansInfo?.specificPlan?.isDedicated === true,
  );

  // Storage add-on: only EBS (v2) sites can grow their disk
  const sitesList = useSelector((state) => state.sitesInfo?.sitesList?.data);
  const ebsSites = (Array.isArray(sitesList) ? sitesList : []).filter(
    (site) => site?.ebsSite === true,
  );
  // staging environments and Performance Sites are v1 features (an EBS site has
  // its own Performance Upgrade instead): once every site of the account is an
  // EBS site those cards are hidden. An empty or not-yet-loaded list keeps them,
  // and so do the orgs that still build v1 sites.
  const allSitesAreEbs =
    !buildsV1Sites(orgId) &&
    Array.isArray(sitesList) &&
    sitesList.length > 0 &&
    sitesList.every((site) => site?.ebsSite === true);
  const storageSiteOptions = ebsSites.map((site) => ({
    label: site.siteName,
    value: site.siteId,
  }));
  const storageSites = ebsSites.filter((site) =>
    storageSiteIds.includes(site.siteId),
  );
  // a site can be upgraded once (single tier); addOns.performanceUpgrade comes from the site overview
  const performanceSiteOptions = ebsSites
    .filter(
      (site) =>
        siteOverviewList?.find((overview) => overview?.siteId === site.siteId)
          ?.addOns?.performanceUpgrade !== true,
    )
    .map((site) => ({ label: site.siteName, value: site.siteId }));
  const performanceSite = ebsSites.find(
    (site) => site.siteId === performanceSiteId,
  );
  // used / limit per selected site (limit already includes storage bought before)
  const storageSitesUsage = storageSites.map((site) => ({
    siteId: site.siteId,
    siteName: site.siteName,
    usage: siteOverviewList
      ?.find((overview) => overview?.siteId === site.siteId)
      ?.featuresUsageRes?.find((feature) => feature?.type === "STORAGE"),
  }));

  const handleHoursChange = (newHours) => {
    setHours(newHours);
  };

  const handlePriceChange = (newPrice) => {
    setPrice(newPrice);
  };

  useEffect(() => {
    getStore().dispatch({
      type: Actions.API_GET_STRIPE_PUBLISHABLE_KEY,
      meta: {
        onSuccess: (data) => {
          const stripePromise = loadStripe(data);
          setStripePromise(stripePromise);
        },
        onError: () => {},
      },
    });
  }, []);

  useEffect(() => {
    getStore().dispatch({
      type: Actions.API_SITES_LIST,
      data: { orgId: orgId ? orgId : -1 },
      meta: { onSuccess: () => {}, onError: () => {} },
    });
    // per-site storage used/limit for the Storage add-on card
    getStore().dispatch({
      type: Actions.API_SITE_OVERVIEW,
      data: { orgId: orgId ? orgId : -1 },
      meta: {
        onSuccess: (data) => {
          if (Array.isArray(data)) {
            setSiteOverviewList(data);
          }
        },
        onError: () => {},
      },
    });
  }, [orgId]);

  useEffect(() => {
    getStore().dispatch({
      type: Actions.GET_UPCOMING_INVOICE,
      orgId: orgId ? orgId : -1,
      country: country === "USA" ? "US" : "IN",
    });
  }, [selectedOrg]);

  useEffect(() => {
    if (allPlans?.features?.licenses && plan?.length) {
      const updatedAddons = allPlans.features.licenses.map((license) => {
        let icon;

        // Match the licenseType with the corresponding icon
        switch (license.licenseType) {
          case "Additional Site":
            icon = <Addon className={AddonPageStyle.addonIcon} />;
            break;
          case "Performance Sites":
            icon = <PerformanceSites className={AddonPageStyle.addonIcon} />;
            break;
          case "Staging":
            icon = <Staging className={AddonPageStyle.addonIcon} />;
            break;
          case "Waf":
            icon = <WAF className={AddonPageStyle.addonIcon} />;
            break;
          case addons_names.STORAGE:
            icon = <StorageIcon className={AddonPageStyle.addonIcon} />;
            break;
          case addons_names.PERFORMANCE_UPGRADE:
            icon = (
              <PerformanceUpgradeIcon className={AddonPageStyle.addonIcon} />
            );
            break;
          default:
            icon = <Addon className={AddonPageStyle.addonIcon} />;
        }

        // Determine salePrice based on country
        const salePrice =
          country === "USA"
            ? license.pricing[0]?.salePrice
            : license.pricing[0]?.salePriceInr;

        const matchingPlan = plan.find(
          (p) => p.licenseType === license.licenseType,
        );

        return {
          icon,
          addonType: license.licenseType,
          addonCount: license.licenseType === "Staging" ? hours : 0,
          addonCost:
            license.licenseType === "Staging"
              ? parseFloat(salePrice) || 0
              : parseFloat(salePrice) || 0,
          // storage and the performance upgrade are bought per site, so they have no account-level count
          existingAddons:
            license.licenseType === addons_names.STORAGE ||
            license.licenseType === addons_names.PERFORMANCE_UPGRADE
              ? 0
              : matchingPlan?.quantity || 0,
          stripePriceId: license.pricing[0]?.stripePriceId,
          // GB in one storage pack = the add-on plan's STORAGE feature value
          ...(license.licenseType === addons_names.STORAGE && {
            packGb:
              parseInt(
                license.features?.find((feature) => feature.type === "STORAGE")
                  ?.limitOrAvailablity,
              ) || 0,
          }),
        };
      });

      setAddonDetails(updatedAddons);
    }
  }, [allPlans, country, plan, hours, price]);

  useMakeApiCalls(() => {
    getStore().dispatch({
      type: Actions.SET_BILL_DETAILS,
      data: {
        orgId: orgId,
        siteId: siteId,
        sitePage: lastSegment,
      },
    });
  });

  const handleAddMore = (addonType) => {
    setAddonDetails((prevAddons) =>
      prevAddons.map((addon) =>
        addon.addonType === addonType
          ? { ...addon, addonCount: addon.addonCount + 1 }
          : addon,
      ),
    );
  };

  const handleRemove = (addonType) => {
    setAddonDetails((prevAddons) =>
      prevAddons.map((addon) =>
        addon.addonType === addonType && addon.addonCount > 0
          ? { ...addon, addonCount: addon.addonCount - 1 }
          : addon,
      ),
    );
  };

  const [popup, setPopup] = useState({
    popupState: false,
    popupType: "",
    popupTitle: "",
    popupSubtitle: "",
    popupSubtitle1: "",
    popupButton: false,
    popupCancel: false,
    popupButtonTitle: "",
    popupButtonFunction: () => {},
  });
  const ActivatePopup = (type, message) => {
    if (type === "info") {
      setPopup({
        popupState: true,
        popupType: "info",
        popupTitle: "Unlock the Best Plans for Your Needs!",
        popupSubtitle:
          "Reach out to connect@roconpaas.com for exclusive offers!",
        popupCancel: false,
        popupButton: true,
        popupButtonTitle: "Ok",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
        },
      });
    } else if (type === "error") {
      setPopup({
        popupState: true,
        popupType: "error",
        popupTitle: "Plan Downgrade Unavailable",
        popupSubtitle:
          "Unable to process your downgrade. Contact support@roconpaas.com for help.",
        popupCancel: false,
        popupButton: true,
        popupButtonTitle: "Ok",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
        },
      });
    } else if (type === "success-upgrade") {
      setPopup({
        popupState: true,
        popupType: "success",
        popupTitle: "Plan Upgrade Successful!",
        popupSubtitle:
          "Your organization is now upgraded. Enjoy your new features!",
        popupCancel: false,
        popupButton: true,
        popupButtonTitle: "Ok",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
        },
      });
    } else if (type === "error-upgrade") {
      setPopup({
        popupState: true,
        popupType: "error",
        popupTitle: "Plan Upgrade Failed!",
        popupSubtitle: "Please try again or contact support for assistance.",
        popupCancel: false,
        popupButton: true,
        popupButtonTitle: "Ok",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
        },
      });
    } else if (type === "error-singlesite") {
      setPopup({
        popupState: true,
        popupType: "error",
        popupTitle: "Plan Upgrade Failed!",
        popupSubtitle:
          "Switching plan types isn’t supported for upgrades. Please select a compatible option.",
        popupCancel: false,
        popupButton: true,
        popupButtonTitle: "Ok",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
        },
      });
    } else if (type === "no-upgrade") {
      setPopup({
        popupState: true,
        popupType: "error",
        popupTitle: "Plan Upgrade Unavailable",
        popupSubtitle:
          "Unable to process your upgrade. Contact support@roconpaas.com for help.",
        popupCancel: false,
        popupButton: true,
        popupButtonTitle: "Ok",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
        },
      });
    } else if (type === "no-addon") {
      setPopup({
        popupState: true,
        popupType: "error",
        popupTitle: "Add-on Incompatibility",
        popupSubtitle: "Add-ons are not supported for single-site plans.",
        popupCancel: false,
        popupButton: true,
        popupButtonTitle: "Ok",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
        },
      });
    } else if (type === "wait-addon") {
      setPopup({
        popupState: true,
        popupType: "info",
        popupTitle: "Upgrade Delayed",
        popupSubtitle:
          "Addons can only be upgraded after 24 hours. Please try again later.",
        popupCancel: false,
        popupButton: true,
        popupButtonTitle: "Ok",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
        },
      });
    }
  };

  return (
    <>
      {upcomingInvoice ? (
        <Grid2 className={AddonPageStyle.container}>
          <Grid2 className={AddonPageStyle.header}>Add-Ons</Grid2>
          <Grid2 className={AddonPageStyle.contentBlock}>
            <Grid2 className={AddonPageStyle.addonBlock}>
              {addonDetails
                .filter(
                  (item) =>
                    item.addonType !== "Additional Site" &&
                    (item.addonType !== addons_names.PERFORMANCE ||
                      (!isSingleSite && !allSitesAreEbs)) &&
                    // per-site EBS add-ons are only offered when there is an EBS site to apply them to
                    (item.addonType !== addons_names.STORAGE ||
                      storageSiteOptions.length > 0) &&
                    (item.addonType !== addons_names.PERFORMANCE_UPGRADE ||
                      performanceSiteOptions.length > 0) &&
                    (item.addonType !== addons_names.STAGING ||
                      !allSitesAreEbs),
                )
                .map((addon, index) => (
                  <Grid2 item key={index} className={AddonPageStyle.gridItem}>
                    <AddonUsage
                      key={index}
                      icon={addon.icon}
                      addonType={addon.addonType}
                      addonCount={addon.addonCount}
                      addonCost={addon.addonCost}
                      existingAddons={addon.existingAddons}
                      onAddMore={() => handleAddMore(addon.addonType)}
                      onRemove={() => handleRemove(addon.addonType)}
                      country={country}
                      onHoursChange={handleHoursChange}
                      onPriceChange={handlePriceChange}
                      addonDetails={addonDetails}
                      limit={limit}
                      stgLimit={stagingLimit}
                      stgUsed={stagingUsed}
                      userDetails={userDetails}
                      dedicatedHosting={dedicatedHosting}
                      storageSiteOptions={storageSiteOptions}
                      storageSiteIds={storageSiteIds}
                      onStorageSiteSelect={setStorageSiteIds}
                      storageSitesUsage={storageSitesUsage}
                      performanceSiteOptions={performanceSiteOptions}
                      performanceSiteId={performanceSiteId}
                      onPerformanceSiteSelect={setPerformanceSiteId}
                    />
                  </Grid2>
                ))}
            </Grid2>
            {!dedicatedHosting && (
              <Grid2 className={AddonPageStyle.summaryBlock}>
                <Elements stripe={stripePromise}>
                  <AddonsBillingSummary
                    navigate={navigate}
                    isAddon={true}
                    billingType={"Monthly"}
                    billingSalePrice={upcomingInvoice?.estimatedBill}
                    addonDetails={addonDetails}
                    country={country}
                    NoAddonPopup={() => ActivatePopup("no-addon")}
                    WaitAddonPopup={() => ActivatePopup("wait-addon")}
                    disabled={isDisabled}
                    formatIndianCurrency={formatIndianCurrency}
                    isSingleSite={isSingleSite}
                    couponCode={paymentInfo?.couponCode}
                    stripeCoupon={paymentInfo?.stripeUpcomingInvoice}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    orgId={orgId}
                    userDetails={userDetails}
                    selectedOrg={selectedOrg}
                    basePlan={basePlan}
                    plan={plan}
                    storageSites={storageSites}
                    performanceSite={performanceSite}
                  />
                </Elements>
              </Grid2>
            )}
          </Grid2>
        </Grid2>
      ) : (
        <PageLoading />
      )}
      <FormPopup
        trigger={popup.popupState}
        setTrigger={setPopup}
        type={popup.popupType}
        title={popup.popupTitle}
        subtitle={popup.popupSubtitle}
        button={popup.popupButton}
        cancel={popup.popupCancel}
        buttonTitle={popup.popupButtonTitle}
        buttonFunction={popup.popupButtonFunction}
      />
    </>
  );
};

export default AddonPage;
